import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';
import ChainedBackend from 'i18next-chained-backend';
import LocalStorageBackend from 'i18next-localstorage-backend';

import translationEN from '../locales/en.json';
import translationSV from '../locales/sv.json';
import translationES from '../locales/es.json';
import translationNB from '../locales/nb.json';
import translationNL from '../locales/nl.json';
import translationDE from '../locales/de.json';
import translationFI from '../locales/fi.json';
import translationDA from '../locales/da.json';
import translationHE from '../locales/he.json';
import translationBG from '../locales/bg.json';
import translationPT from '../locales/pt.json';
import { config } from '../utils/config';
import apis from '../utils/apis';

// Default manifest values - app will start with these if manifest fetch fails
const DEFAULT_MANIFEST = {
  version: '1',
  languages: ['en', 'sv', 'es', 'nb', 'nl', 'de', 'fi', 'da', 'he', 'bg', 'pt'],
  namespaces: ['translation'],
  defaultLocale: 'en'
};

// Fetch manifest in the background - non-blocking
async function fetchManifest() {
  try {
    const resp = await apis({
      method: "GET",
      url: "/i18n/manifest",
      timeout: 5000
    });
    return resp.data || DEFAULT_MANIFEST;
  } catch (error) {
    console.warn('Failed to fetch i18n manifest, using defaults:', error);
    return DEFAULT_MANIFEST;
  }
}

// ─── Text direction (RTL) ────────────────────────────────────────────────────
// Languages that render right-to-left. Hebrew ships today; the others are listed
// so adding them later needs no extra direction wiring.
export const RTL_LANGUAGES = ['he', 'ar', 'fa', 'ur'];

/** True when the (base) language code renders right-to-left. */
export function isRTL(lng?: string): boolean {
  const base = (lng || i18n.language || 'en').split('-')[0].toLowerCase();
  return RTL_LANGUAGES.includes(base);
}

/**
 * Reflect the active language's direction onto <html>: sets `dir` (rtl/ltr) and
 * `lang`. This is the SINGLE switch the whole RTL system keys off - Tailwind's
 * logical utilities, CSS logical properties and the MUI/Emotion cache all read
 * `dir` from here. Called on init and on every languageChanged, so direction
 * always follows the active language with no per-component wiring.
 */
export function applyDocumentDirection(lng?: string): void {
  if (typeof document === 'undefined') return;
  const base = (lng || i18n.language || 'en').split('-')[0].toLowerCase();
  const root = document.documentElement;
  root.setAttribute('dir', isRTL(base) ? 'rtl' : 'ltr');
  root.setAttribute('lang', base);
}

export async function initI18n() {
  // Use DEFAULT_MANIFEST immediately for app startup
  const manifest = DEFAULT_MANIFEST;

  const version = String(manifest.version || '1');
  const supportedLngs = manifest.languages || ['en'];
  const namespaces = manifest.namespaces || ['translation'];
  const fallbackLng = manifest.defaultLocale || 'en';
  const storedLanguage = localStorage.getItem('language') || fallbackLng;

  await i18n
    .use(ChainedBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      lng: storedLanguage,
      fallbackLng,
      supportedLngs,
      ns: namespaces,
      defaultNS: 'translation',
      load: 'currentOnly',
      backend: {
        backends: [LocalStorageBackend, HttpBackend],
        backendOptions: [
          {
            expirationTime: 7 * 24 * 60 * 60 * 1000,
            versions: supportedLngs.reduce((acc: any, lng: string) => {
              acc[lng] = version;
              return acc;
            }, {})
          },
          {
            loadPath: `${config.baseUrl}/i18n/{{lng}}/{{ns}}.json`,
            requestOptions: { credentials: 'include' }
          }
        ]
      },
      resources: {
        en: {
          translation: translationEN,
        },
        sv: {
          translation: translationSV,
        },
        es: {
          translation: translationES,
        },
        nb: {
          translation: translationNB,
        },
        nl: {
          translation: translationNL,
        },
        de: {
          translation: translationDE,
        },
        fi: {
          translation: translationFI,
        },
        da: {
          translation: translationDA,
        },
        he: {
          translation: translationHE,
        },
        bg: {
          translation: translationBG,
        },
        pt: {
          translation: translationPT,
        },
      },
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: true,
      },
      debug: false,
      detection: { order: ['navigator'], caches: [] }
    },
    (err, t) => {
      if (err) return 
      t('key');
    })

  i18n.on('initialized', () => {
    // First paint must already carry the right direction, so apply it here as
    // well as on languageChanged (which does not fire when the stored language
    // equals the init language).
    applyDocumentDirection(i18n.language);
  })
  i18n.on('languageChanged', (lng) => {
    localStorage.setItem('language', lng);
    applyDocumentDirection(lng);
  });

  i18n.on('loaded', (loaded) => {
  });

  i18n.on('failedLoading', (lng, ns, msg) => {
  });

  // Fetch the manifest in the background (non-blocking) to update supported languages
  // fetchManifest().then(updatedManifest => {
  //   if (updatedManifest && updatedManifest.languages) {
  //     i18n.services.backendConnector.language = updatedManifest.languages;
  //   }
  // }).catch(() => {
  //   // Silently ignore errors - app already started with defaults
  // });

  // The 'initialized' handler above can be attached after init has already
  // resolved (so the event is missed), and 'languageChanged' does not fire when
  // the cached language equals the init language. So apply direction directly
  // here too - this is what guarantees a refresh in Hebrew renders RTL.
  applyDocumentDirection(i18n.language);

  return i18n;
}


export async function changeLanguage(lng: string) {

  await i18n.loadLanguages(lng);
  await i18n.reloadResources([lng], ['translation']);  

  await i18n.changeLanguage(lng);
}
